/**
 * ===============================================
 * ARKUNO Wish Button Script
 * 売り切れ・Coming Soon商品「欲しい！」機能
 * ===============================================
 */

(function() {
  'use strict';

  // Constants
  const STORAGE_KEY = 'arkuno_wish_history';
  const STORAGE_EXPIRY_DAYS = 7;
  const TOAST_DURATION = 4000;

  class WishButton {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.productId = wrapper.dataset.productId;
      this.variantId = wrapper.dataset.variantId;
      this.wishContext = wrapper.dataset.wishContext || 'sold_out';

      this.button = wrapper.querySelector('[data-wish-trigger]');
      this.modal = wrapper.querySelector('[data-wish-modal]');
      this.form = wrapper.querySelector('[data-wish-form]');
      this.successView = wrapper.querySelector('[data-wish-success]');
      this.toast = wrapper.querySelector('[data-wish-toast]');
      this.priceField = wrapper.querySelector('[data-price-field]');

      this.init();
    }

    init() {
      if (!this.button || !this.modal || !this.form) {
        console.error('Wish button: Required elements not found');
        return;
      }

      // Check if already sent
      this.checkSentStatus();

      // Event listeners
      this.button.addEventListener('click', () => this.openModal());

      this.modal.querySelectorAll('[data-wish-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeModal());
      });

      this.modal.querySelector('.ark-wish-modal__overlay').addEventListener('click', () => this.closeModal());

      this.form.addEventListener('submit', (e) => this.handleSubmit(e));

      // Price field toggle for coming_soon context
      if (this.priceField) {
        this.form.querySelectorAll('input[name="reason"]').forEach(radio => {
          radio.addEventListener('change', () => this.togglePriceField());
        });
      }

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.closeModal();
        }
      });
    }

    togglePriceField() {
      if (!this.priceField) return;
      const selectedReason = this.form.querySelector('input[name="reason"]:checked');
      this.priceField.hidden = !selectedReason || selectedReason.value !== 'price_suggestion';
    }

    checkSentStatus() {
      const history = this.getWishHistory();
      const key = `${this.productId}_${this.variantId}`;
      const sent = history[key];

      if (sent && !this.isExpired(sent.timestamp)) {
        this.button.classList.add('sent');
        this.button.disabled = true;
        this.button.textContent = window.wishButtonTranslations?.button_sent || 'いいね済み';
      }
    }

    getWishHistory() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : {};
      } catch (e) {
        console.error('Failed to read wish history:', e);
        return {};
      }
    }

    saveWishHistory(email, reason) {
      try {
        const history = this.getWishHistory();
        const key = `${this.productId}_${this.variantId}`;

        history[key] = {
          timestamp: Date.now(),
          email: email ? this.hashEmail(email) : '',
          reason: reason || 'same_design'
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
      } catch (e) {
        console.error('Failed to save wish history:', e);
      }
    }

    isExpired(timestamp) {
      const expiry = STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      return Date.now() - timestamp > expiry;
    }

    hashEmail(email) {
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        const char = email.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return hash.toString(36);
    }

    openModal() {
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');

      const modalContent = this.modal.querySelector('.ark-wish-modal__content');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }

      const firstInput = this.form.querySelector('input[type="email"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }

      this.trackEvent('wish_modal_open');
    }

    closeModal() {
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');

      setTimeout(() => {
        this.resetForm();
      }, 300);
    }

    resetForm() {
      this.form.reset();
      this.form.hidden = false;
      this.successView.hidden = true;
      if (this.priceField) this.priceField.hidden = true;
      this.clearErrors();
    }

    async handleSubmit(e) {
      e.preventDefault();

      this.clearErrors();

      const validation = this.validateForm();
      if (!validation.valid) {
        this.showErrors(validation.errors);
        return;
      }

      const formData = new FormData(this.form);
      const reason = formData.get('reason') || (this.wishContext === 'coming_soon' ? 'notify_on_release' : 'same_design');
      const data = {
        email: formData.get('email') || '',
        reason: reason,
        productId: this.productId,
        variantId: this.variantId,
        productTitle: this.getProductTitle(),
        productUrl: window.location.href,
        type: this.wishContext === 'coming_soon' ? 'interest' : 'restock',
        desiredPrice: formData.get('desired_price') || null
      };

      // Show loading
      this.form.classList.add('loading');
      const submitBtn = this.form.querySelector('[data-wish-submit]');
      const originalText = submitBtn.textContent;
      submitBtn.textContent = window.wishButtonTranslations?.sending || '送信中...';
      submitBtn.disabled = true;

      try {
        await this.sendWishRequest(data);

        this.saveWishHistory(data.email, data.reason);

        this.trackEvent('wish_submit_success', {
          product_id: this.productId,
          variant_id: this.variantId,
          reason: data.reason,
          type: data.type
        });

        this.showSuccess();
        this.checkSentStatus();

      } catch (error) {
        console.error('Wish submission error:', error);

        this.trackEvent('wish_submit_error', {
          error: error.message
        });

        this.showToast(
          window.wishButtonTranslations?.error_message || '送信に失敗しました。しばらくしてから再度お試しください。',
          'error'
        );
      } finally {
        this.form.classList.remove('loading');
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
    }

    validateForm() {
      const errors = {};
      const formData = new FormData(this.form);
      const email = formData.get('email');

      if (this.wishContext === 'coming_soon') {
        // coming_soon: email required
        if (!email || !email.trim()) {
          errors.email = window.wishButtonTranslations?.email_required || '通知にはメールアドレスが必要です';
        } else if (!this.isValidEmail(email)) {
          errors.email = window.wishButtonTranslations?.email_invalid || '有効なメールアドレスを入力してください';
        }
      } else {
        // sold_out: email optional, but validate format if provided
        if (email && !this.isValidEmail(email)) {
          errors.email = window.wishButtonTranslations?.email_invalid || '有効なメールアドレスを入力してください';
        }
      }

      return {
        valid: Object.keys(errors).length === 0,
        errors: errors
      };
    }

    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    }

    showErrors(errors) {
      Object.keys(errors).forEach(field => {
        const errorEl = this.form.querySelector(`[data-error="${field}"]`);
        if (errorEl) {
          errorEl.textContent = errors[field];
          const input = this.form.querySelector(`[name="${field}"]`);
          if (input) {
            input.classList.add('error');
          }
        }
      });

      const firstError = Object.keys(errors)[0];
      const firstInput = this.form.querySelector(`[name="${firstError}"]`);
      if (firstInput) {
        firstInput.focus();
      }
    }

    clearErrors() {
      this.form.querySelectorAll('[data-error]').forEach(el => {
        el.textContent = '';
      });
      this.form.querySelectorAll('.error').forEach(el => {
        el.classList.remove('error');
      });
    }

    async sendWishRequest(data) {
      const payload = {
        product_id: parseInt(data.productId),
        variant_id: parseInt(data.variantId) || null,
        email: data.email,
        reason: data.reason,
        consent: true,
        type: data.type,
        desired_price: data.desiredPrice ? parseFloat(data.desiredPrice) : null,
        product_title: data.productTitle,
        product_url: data.productUrl
      };

      // Send to Go API (r.arkuno.jp)
      const apiUrl = this.form.dataset.apiUrl || 'https://r.arkuno.jp/api/wish';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Request failed';
        try {
          const err = await response.json();
          errorMessage = err.error || errorMessage;
        } catch (_) {
          // ignore parse error
        }
        throw new Error(errorMessage);
      }

      return response.json();
    }

    showSuccess() {
      this.form.hidden = true;
      this.successView.hidden = false;
    }

    showToast(message, type = 'info') {
      if (!this.toast) return;

      this.toast.textContent = message;
      this.toast.classList.remove('error');
      if (type === 'error') {
        this.toast.classList.add('error');
      }

      this.toast.classList.add('show');

      setTimeout(() => {
        this.toast.classList.remove('show');
      }, TOAST_DURATION);
    }

    getProductTitle() {
      const titleEl = document.querySelector('.product__title, [itemprop="name"], h1');
      if (!titleEl) return 'Unknown Product';

      return titleEl.textContent
        .replace(/\s+/g, ' ')
        .trim();
    }

    trackEvent(eventName, params = {}) {
      // Google Analytics 4
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, {
          event_category: 'Wish Button',
          ...params
        });
      }

      // Meta Pixel (Facebook)
      if (typeof fbq !== 'undefined') {
        fbq('trackCustom', eventName, params);
      }

      console.log('Wish event:', eventName, params);
    }
  }

  // Initialize all wish buttons on the page
  function initWishButtons() {
    const wrappers = document.querySelectorAll('.ark-wish-wrapper');
    wrappers.forEach(wrapper => {
      new WishButton(wrapper);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishButtons);
  } else {
    initWishButtons();
  }

  // Re-initialize on Shopify section render (for theme editor)
  document.addEventListener('shopify:section:load', initWishButtons);
})();
